import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Card, CardContent, Grid, Alert, Snackbar,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, CircularProgress, Switch, Select, MenuItem, FormControl, InputLabel,
  IconButton, FormControlLabel, Collapse, InputAdornment, Tooltip,
} from '@mui/material';
import {
  Refresh, Add, Delete, Casino, Warning, KeyboardArrowDown, KeyboardArrowUp,
} from '@mui/icons-material';
import { adminAPI } from '../../services/api';

/**
 * 🎟️ Вкладка «Слоты/Казино» — универсальные промокоды.
 * Бандлы наград (7 типов), условия новизны, вагер-флаг, отложенный старт МСК.
 * Авто-генерация кода. Дрилдаун: клик по коду → активаторы с аналитикой
 * (выигрыш, состояние вагера юзера, IP + флаг мультиаккаунтов).
 * Память формы: localStorage (casino_promo_form_v1).
 */

const LS_KEY = 'casino_promo_form_v1';

const SLOT_OPTIONS = [
  { value: '', label: 'Любой слот (юзер выбирает)' },
  { value: 'sweet_sperm', label: 'Sweet Sperm' },
  { value: 'moon_dogs', label: 'Moon Dogs' },
  { value: 'ton_reel', label: 'TON Reel' },
  { value: 'ton_ranger', label: 'TON Ranger' },
  { value: 'moontoonz', label: 'MoonToonz' },
  { value: 'book_of_moon', label: 'Book of Moon' },
  { value: 'moon_hunter', label: 'Moon Hunter' },
  { value: 'kaguya', label: 'Kaguya Ways' },
];
const CHEST_OPTIONS = [
  { value: 'bronze', label: 'Бронзовый' }, { value: 'silver', label: 'Серебряный' },
  { value: 'gold', label: 'Золотой' }, { value: 'platinum', label: 'Платиновый' },
];
const REWARD_DEFS = {
  freespins:       { label: '🎰 Фриспины слотов', defaults: { count: 10, bet: 0.1, slot: '', expiresInDays: 7 } },
  sperm:           { label: '💰 SPERM', defaults: { amount: 100 } },
  wheel_spins:     { label: '🎡 Спины колеса', defaults: { count: 1 } },
  chest:           { label: '🎁 Сундук', defaults: { chestCode: 'bronze' } },
  free_buy_bonus:  { label: '🛒 Бесплатная бонусная игра', defaults: { count: 1 } },
  aviator_flights: { label: '✈️ Полёты в Авиаторе', defaults: { count: 10, bet: 0.1, expiresInDays: 7 } },
  deposit_bonus:   { label: '💎 Депозит-бонус %', defaults: { minAmount: 1000, capAmount: 2000, percent: 10 } },
  wiggle_tickets:  { label: '🎟 Боевые билеты Wiggle Wars', defaults: { count: 3, stake: 50, expiresInDays: 7 } },
};
// ставки билетов — как в игре (casino/WIGGLE_WARS/server/src/money.ts STAKES)
const WIGGLE_STAKES = [50, 100, 200, 300, 500];

const DEFAULT_FORM = {
  name: '', code: '', maxActivations: 100, newUsersOnly: false,
  wagerRequired: true, startNow: true, validFromMsk: '', validDays: 14, rewards: [],
};

// Авто-код: 6 случайных букв/цифр БЕЗ префикса и подчёркивания
// (легко рисовать на картинках). Без похожих 0/O/1/I.
function genCode() {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let s = '';
  for (let i = 0; i < 6; i++) s += alphabet[Math.floor(Math.random() * alphabet.length)];
  return s;
}

const fmtInt = (n) => Math.round(Number(n) || 0).toLocaleString('ru-RU');
const fmt2 = (n) => (Number(n) || 0).toFixed(2);

// Сводка «что юзер выиграл/получил» по активации.
function wonSummary(a) {
  const parts = [];
  if (a.av_total != null) {
    const st = { active: 'летает', completed: 'готово', expired: 'сгорел' }[a.av_status] || a.av_status;
    parts.push(`✈️ ${fmt2(a.av_win)} (${a.av_used}/${a.av_total}, ${st})`);
  }
  if (a.deposit_status === 'completed') parts.push(`💎 +${fmt2(a.deposit_bonus)}`);
  else if (a.deposit_status === 'pending') parts.push('💎 ждёт депозит');
  const sperm = (a.rewards_granted || []).find((r) => r.type === 'sperm');
  if (sperm) parts.push(`💰 ${fmtInt(sperm.amount)}`);
  return parts.length ? parts.join(' · ') : '—';
}

// Состояние вагера ЮЗЕРА (агрегатное — MoonStash общий пул, не по промику).
function wagerChip(a) {
  const stash = Number(a.stash_balance) || 0;
  const req = Number(a.loyalty_wager_required) || 0;
  if (req > 0) return { text: `отыгрывает −${fmtInt(req)}`, color: 'warning' };
  if (stash > 0) return { text: `в копилке ${fmtInt(stash)}`, color: 'info' };
  return { text: '—', color: 'default' };
}

function loadSavedForm() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return DEFAULT_FORM;
    const saved = JSON.parse(raw);
    // код и название всегда чистые — это уникальные поля нового промика;
    // при галочке «для новых» отложенный старт не применяется
    // старт по умолчанию ВСЕГДА «сразу»: дата отложенного старта не запоминается (владелец 01.10 —
    // сохранённая дата молча откладывала новый промокод на чужой день)
    const merged = { ...DEFAULT_FORM, ...saved, name: '', code: '', startNow: true, validFromMsk: '' };
    return merged;
  } catch { return DEFAULT_FORM; }
}

function CasinoPromoTab() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [form, setForm] = useState(loadSavedForm);
  // Дрилдаун: какой код раскрыт + кэш активаций по коду + загрузка.
  const [expanded, setExpanded] = useState(null);
  const [actByCode, setActByCode] = useState({});   // promoId → массив активаций
  const [actLoading, setActLoading] = useState(null); // promoId в загрузке

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  // Память формы: каждое изменение → localStorage
  useEffect(() => {
    try { localStorage.setItem(LS_KEY, JSON.stringify(form)); } catch {}
  }, [form]);

  const loadCodes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getCasinoPromoCodes();
      setCodes(res.data.data || []);
    } catch (e) {
      notify(e.response?.data?.error || 'Ошибка загрузки промокодов', 'error');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadCodes(); }, [loadCodes]);

  // Загрузка активаций конкретного кода (для дрилдауна), с кэшем.
  const fetchCodeActivations = useCallback(async (promoId, force = false) => {
    if (!force && actByCode[promoId]) return;
    setActLoading(promoId);
    try {
      const res = await adminAPI.getCasinoPromoActivations(promoId);
      setActByCode((prev) => ({ ...prev, [promoId]: res.data.data || [] }));
    } catch (e) {
      notify(e.response?.data?.error || 'Ошибка загрузки активаций', 'error');
    } finally { setActLoading(null); }
  }, [actByCode]);

  const toggleExpand = (promoId) => {
    if (expanded === promoId) { setExpanded(null); return; }
    setExpanded(promoId);
    fetchCodeActivations(promoId);
  };

  // ── Награды-бандл ──
  const addReward = (type) => {
    if (!type || form.rewards.some((r) => r.type === type)) return;
    setForm({ ...form, rewards: [...form.rewards, { type, ...REWARD_DEFS[type].defaults }] });
  };
  const patchReward = (i, patch) => {
    const rewards = form.rewards.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    setForm({ ...form, rewards });
  };
  const removeReward = (i) => setForm({ ...form, rewards: form.rewards.filter((_, idx) => idx !== i) });

  const handleCreate = async () => {
    if (!form.name.trim()) return notify('Укажите название', 'error');
    if (!form.code.trim()) return notify('Укажите код', 'error');
    if (form.rewards.length === 0) return notify('Добавьте хотя бы одну награду', 'error');
    setCreating(true);
    try {
      // числовые поля наград приводим к числам, slot '' → null
      const rewards = form.rewards.map((r) => {
        const o = { ...r };
        ['count', 'bet', 'amount', 'expiresInDays', 'minAmount', 'capAmount', 'percent', 'stake']
          .forEach((k) => { if (o[k] !== undefined && o[k] !== '') o[k] = Number(o[k]); });
        if (o.type === 'freespins') o.slot = o.slot || null;
        return o;
      });
      await adminAPI.createCasinoPromoCode({
        name: form.name.trim(), code: form.code.trim(), rewards,
        maxActivations: Number(form.maxActivations) || 100,
        newUsersOnly: !!form.newUsersOnly,
        wagerRequired: !!form.wagerRequired,
        validFromMsk: (form.newUsersOnly || form.startNow) ? null : (form.validFromMsk || null),
        validDays: Number(form.validDays) || 14,
      });
      notify(`Промокод «${form.code.trim().toUpperCase()}» создан`);
      setForm({ ...form, name: '', code: '' });   // настройки остаются (память), имя/код чистим
      loadCodes();
    } catch (e) {
      notify(e.response?.data?.error || 'Ошибка создания', 'error');
    } finally { setCreating(false); }
  };

  const handleToggle = async (row) => {
    try {
      await adminAPI.toggleCasinoPromoCode(row.id, !row.is_active);
      setCodes((prev) => prev.map((c) => (c.id === row.id ? { ...c, is_active: !c.is_active } : c)));
    } catch (e) {
      notify(e.response?.data?.error || 'Ошибка переключения', 'error');
    }
  };

  const handleDelete = async (row) => {
    const used = row.activations_used || 0;
    const warn = used > 0
      ? `\n\nВНИМАНИЕ: код активирован ${used} раз — удалятся активации и связанные фрибеты. Уже выданные награды (SPERM/сундуки/спины) НЕ отзываются.`
      : '';
    if (!window.confirm(`Удалить промокод «${row.code}»?${warn}`)) return;
    try {
      await adminAPI.deleteCasinoPromoCode(row.id);
      setCodes((prev) => prev.filter((c) => c.id !== row.id));
      notify(`Промокод «${row.code}» удалён`, 'success');
    } catch (e) {
      notify(e.response?.data?.error || 'Ошибка удаления', 'error');
    }
  };

  const statusChip = (c) => {
    if (!c.is_active) return <Chip size="small" label="Выключен" color="default" />;
    if (c.not_started) {
      const d = new Date(c.valid_from);
      const p2 = (n) => String(n).padStart(2, '0');
      return <Chip size="small" label={`с ${p2(d.getDate())}.${p2(d.getMonth() + 1)} ${p2(d.getHours())}:${p2(d.getMinutes())}`} color="info" />;
    }
    if (c.is_expired) return <Chip size="small" label="Истёк" color="warning" />;
    if (c.activations_used >= c.max_activations) return <Chip size="small" label="Лимит" color="error" />;
    return <Chip size="small" label="Активен" color="success" />;
  };

  const rewardsSummary = (rewards) => (rewards || []).map((r) => {
    switch (r.type) {
      case 'freespins': return `🎰×${r.count}@${r.bet}`;
      case 'sperm': return `💰${r.amount}`;
      case 'wheel_spins': return `🎡×${r.count}`;
      case 'chest': return `🎁${r.chestCode}`;
      case 'free_buy_bonus': return `🛒×${r.count}`;
      case 'aviator_flights': return `✈️×${r.count}@${r.bet}`;
      case 'deposit_bonus': return `💎${r.percent}% (${r.minAmount}–${r.capAmount})`;
      case 'wiggle_tickets': return `🎟×${r.count}@${r.stake}`;
      default: return r.type;
    }
  }).join(' · ');

  // ── Поля параметров награды ──
  const rewardFields = (r, i) => {
    const tf = (key, label, width = 110) => (
      <TextField size="small" type="number" label={label} value={r[key]}
                 onChange={(e) => patchReward(i, { [key]: e.target.value })} sx={{ width }} />
    );
    switch (r.type) {
      case 'freespins': return (<>
        {tf('count', 'Кол-во')}{tf('bet', 'Ставка')}
        <FormControl size="small" sx={{ minWidth: 190 }}>
          <InputLabel>Слот</InputLabel>
          <Select label="Слот" value={r.slot} onChange={(e) => patchReward(i, { slot: e.target.value })}>
            {SLOT_OPTIONS.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
          </Select>
        </FormControl>
        {tf('expiresInDays', 'Срок, дн.')}
      </>);
      case 'sperm': return tf('amount', 'Сумма SPERM', 140);
      case 'wheel_spins': return tf('count', 'Кол-во');
      case 'chest': return (
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Сундук</InputLabel>
          <Select label="Сундук" value={r.chestCode} onChange={(e) => patchReward(i, { chestCode: e.target.value })}>
            {CHEST_OPTIONS.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
          </Select>
        </FormControl>);
      case 'free_buy_bonus': return tf('count', 'Кол-во');
      case 'aviator_flights': return (<>{tf('count', 'Полётов')}{tf('bet', 'Ставка (0.1–2)', 130)}{tf('expiresInDays', 'Срок, дн.')}</>);
      case 'deposit_bonus': return (<>{tf('minAmount', 'Депозит от', 120)}{tf('capAmount', 'Кап (до)', 120)}{tf('percent', 'Бонус, %')}</>);
      case 'wiggle_tickets': return (<>
        {tf('count', 'Билетов')}
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Ставка</InputLabel>
          <Select label="Ставка" value={r.stake} onChange={(e) => patchReward(i, { stake: e.target.value })}>
            {WIGGLE_STAKES.map((s) => <MenuItem key={s} value={s}>{s} SPERM</MenuItem>)}
          </Select>
        </FormControl>
        {tf('expiresInDays', 'Срок, дн.')}
      </>);
      default: return null;
    }
  };

  // ── Детейл активаторов кода (дрилдаун) ──
  const renderActivations = (promoId) => {
    if (actLoading === promoId && !actByCode[promoId]) {
      return <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={22} /></Box>;
    }
    const rows = actByCode[promoId] || [];
    if (rows.length === 0) {
      return <Typography variant="body2" color="text.secondary" sx={{ py: 1.5, textAlign: 'center' }}>Активаций пока нет</Typography>;
    }
    return (
      <TableContainer>
        <Table size="small" sx={{ '& th, & td': { px: 0.5, py: 0.5, fontSize: '0.7rem', whiteSpace: 'nowrap' } }}>
          <TableHead>
            <TableRow>
              <TableCell>Юзер</TableCell><TableCell>Выигрыш / получено</TableCell>
              <TableCell>Вагер юзера</TableCell><TableCell>IP</TableCell><TableCell>Когда</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((a) => {
              const multi = a.users_on_ip >= 2;
              const wg = wagerChip(a);
              return (
                <TableRow key={a.id} sx={multi ? { bgcolor: 'rgba(244,67,54,0.10)' } : undefined}>
                  <TableCell>{a.username ? '@' + a.username : (a.first_name || `ID ${a.user_id}`)}</TableCell>
                  <TableCell>{wonSummary(a)}</TableCell>
                  <TableCell>
                    <Chip size="small" color={wg.color} label={wg.text} sx={{ height: 18, fontSize: '0.62rem' }} />
                  </TableCell>
                  <TableCell>
                    {a.ip_address || '—'}
                    {multi && (
                      <Tooltip title={`С этого IP активировали промокоды ${a.users_on_ip} разных аккаунтов`}>
                        <Chip size="small" color="error" icon={<Warning sx={{ fontSize: 13 }} />}
                              label={`${a.users_on_ip} акк`} sx={{ ml: 0.5, height: 18, fontSize: '0.6rem' }} />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>{new Date(a.activated_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        Универсальные промокоды слотов: бандлы наград, условия, вагер. Один юзер активирует
        конкретный код один раз. Награды с вагером идут через MoonStash (отыгрыш по правилам «Забрать», сейчас x20). Выигрыш боевыми билетами Wiggle — всегда в MoonStash.
        Клик по коду в таблице раскрывает активаторов с аналитикой. «Вагер юзера» —
        агрегатное состояние (MoonStash — общий пул, не по конкретному промику).
        Настройки формы запоминаются.
      </Alert>

      {/* Создание */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Add fontSize="small" /> Новый промокод
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Название (для админки)" value={form.name}
                         onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Запуск Авиатора" />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth label="Код" value={form.code}
                         onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                         placeholder="AVIATOR_10"
                         InputProps={{
                           endAdornment: (
                             <InputAdornment position="end">
                               <Tooltip title="Сгенерировать код">
                                 <IconButton edge="end" size="small"
                                             onClick={() => setForm({ ...form, code: genCode() })}>
                                   <Casino fontSize="small" />
                                 </IconButton>
                               </Tooltip>
                             </InputAdornment>
                           ),
                         }} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField fullWidth type="number" label="Лимит активаций" value={form.maxActivations}
                         onChange={(e) => setForm({ ...form, maxActivations: e.target.value })} />
            </Grid>
            <Grid item xs={6} sm={3} sx={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip title="Вкл = активируют только аккаунты, созданные ПОСЛЕ создания промокода. Выкл = для всех.">
                <FormControlLabel
                  control={<Switch checked={!!form.newUsersOnly}
                                   onChange={(e) => setForm({
                                     ...form, newUsersOnly: e.target.checked,
                                     // «для новых» = старт сразу, отложенный старт не совместим
                                     startNow: e.target.checked ? true : form.startNow,
                                     validFromMsk: e.target.checked ? '' : form.validFromMsk,
                                   })} />}
                  label="Только для новых игроков" />
              </Tooltip>
            </Grid>
            {/* Старт: по умолчанию «сразу» — переключатель; дата нужна только для отложенного старта */}
            <Grid item xs={6} sm={3} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={<Switch checked={!!form.startNow || !!form.newUsersOnly} disabled={!!form.newUsersOnly}
                                 onChange={(e) => setForm({ ...form, startNow: e.target.checked,
                                   validFromMsk: e.target.checked ? '' : form.validFromMsk })} />}
                label="Старт сразу" /> {/* подпись постоянная: выкл = отложенный старт (ниже появляется дата) */}
            </Grid>
            {!form.startNow && !form.newUsersOnly && (
              <Grid item xs={12} sm={4}>
                <TextField fullWidth type="datetime-local" label="Старт (МСК)"
                           InputLabelProps={{ shrink: true }} value={form.validFromMsk}
                           onChange={(e) => setForm({ ...form, validFromMsk: e.target.value })}
                           helperText={form.validFromMsk ? 'Промокод заработает в это время' : 'Выберите дату и время старта'} />
              </Grid>
            )}
            <Grid item xs={6} sm={2}>
              <TextField fullWidth type="number" label="Срок, дней" value={form.validDays}
                         onChange={(e) => setForm({ ...form, validDays: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={<Switch checked={!!form.wagerRequired}
                                 onChange={(e) => setForm({ ...form, wagerRequired: e.target.checked })} />}
                label="С вагером (MoonStash, отыгрыш x20)" />
            </Grid>
          </Grid>

          {/* Бандл наград */}
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Награды (бандл):</Typography>
          {form.rewards.map((r, i) => (
            <Box key={r.type} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
                                    p: 1, mb: 1, border: '1px solid rgba(255,255,255,0.12)', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ minWidth: 200 }}>{REWARD_DEFS[r.type].label}</Typography>
              {rewardFields(r, i)}
              <IconButton size="small" color="error" onClick={() => removeReward(i)}><Delete fontSize="small" /></IconButton>
            </Box>
          ))}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1 }}>
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel>+ Добавить награду</InputLabel>
              <Select label="+ Добавить награду" value=""
                      onChange={(e) => addReward(e.target.value)}>
                {Object.entries(REWARD_DEFS)
                  .filter(([t]) => !form.rewards.some((r) => r.type === t))
                  .map(([t, def]) => <MenuItem key={t} value={t}>{def.label}</MenuItem>)}
              </Select>
            </FormControl>
            <Button variant="contained" onClick={handleCreate} disabled={creating}
                    startIcon={creating ? <CircularProgress size={18} /> : <Add />}>
              Создать
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Список кодов + дрилдаун активаций */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Typography variant="h6">Все промокоды (клик по строке — активаторы)</Typography>
          <Button startIcon={<Refresh />} onClick={() => { loadCodes(); if (expanded) fetchCodeActivations(expanded, true); }} disabled={loading}>Обновить</Button>
        </Box>
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
        ) : codes.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>Промокодов пока нет</Typography>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ '& th, & td': { px: 0.5, py: 0.6, fontSize: '0.72rem', whiteSpace: 'nowrap' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 32 }} />
                  <TableCell>Код</TableCell><TableCell>Название</TableCell><TableCell>Награды</TableCell>
                  <TableCell align="center">Вагер</TableCell><TableCell align="center">Новизна</TableCell>
                  <TableCell align="center">Актив.</TableCell>
                  <TableCell align="center">Попытки</TableCell><TableCell>До</TableCell>
                  <TableCell align="center">Статус</TableCell><TableCell align="center">Вкл</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {codes.map((c) => (
                  <React.Fragment key={c.id}>
                    <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggleExpand(c.id)}>
                      <TableCell>
                        <IconButton size="small">
                          {expanded === c.id ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                        </IconButton>
                      </TableCell>
                      <TableCell><strong>{c.code}</strong></TableCell>
                      <TableCell>{c.name}</TableCell>
                      <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>{rewardsSummary(c.rewards)}</TableCell>
                      <TableCell align="center">{c.wager_required ? 'x20' : '—'}</TableCell>
                      <TableCell align="center">{c.new_users_only ? 'новые' : (c.new_user_max_age_days == null ? 'все' : `≤${c.new_user_max_age_days}д`)}</TableCell>
                      <TableCell align="center">{c.activations_used}/{c.max_activations}</TableCell>
                      <TableCell align="center">
                        {c.failed_attempts > 0 ? (
                          <Tooltip title={`Неудачных вводов всего: ${c.failed_attempts} · за 24ч: ${c.failed_attempts_24h}`}>
                            <span style={{ color: '#ff8a8a', fontWeight: 600, cursor: 'help' }}>
                              ✕{c.failed_attempts}{c.failed_attempts_24h > 0 ? ` (${c.failed_attempts_24h})` : ''}
                            </span>
                          </Tooltip>
                        ) : (
                          <span style={{ opacity: 0.35 }}>—</span>
                        )}
                      </TableCell>
                      <TableCell>{new Date(c.valid_until).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' })}</TableCell>
                      <TableCell align="center">{statusChip(c)}</TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Switch checked={!!c.is_active} onChange={() => handleToggle(c)} size="small" />
                        <Tooltip title="Удалить промокод">
                          <IconButton size="small" color="error" onClick={() => handleDelete(c)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={11} sx={{ p: 0, border: 0 }}>
                        <Collapse in={expanded === c.id} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.02)' }}>
                            {renderActivations(c.id)}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default CasinoPromoTab;
